import * as React from 'react';
import { PointTooltipProps, ResponsiveLine } from '@nivo/line';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { formatCurrency, getCurrencySymbol } from 'helpers/common';

export interface IGraphData {
	id: string;
	color: string;
	data: {
		x: string;
		y: number;
	}[];
}

interface IProps {
	data: IGraphData[];
	type: 'appointments' | 'sales';
	currencyKey: number;

	t?: (key: string) => string;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class SalesInfoGraph extends React.Component<IProps> {
	constructor(props) {
		super(props);

		this.generateGridYValues = this.generateGridYValues.bind(this);
		this.yScaleLabels = this.yScaleLabels.bind(this);
		this.minValue = this.minValue.bind(this);
		this.maxValue = this.maxValue.bind(this);
	}

	/////////////////////////////////////////////////////////////
	// Graph functions
	/////////////////////////////////////////////////////////////
	private generateGridYValues(data: IGraphData): { scaleYValues: number[]; totalValue: number } {
		const availableValues =
			this.props.type === 'sales'
				? [50, 100, 250, 500, 1000, 2000, 4000, 8000, 16000, 32000, 64000, 128000, 256000, 512000]
				: [5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 20000, 50000, 100000, 250000, 500000];
		let smallestValue = 10000000000000;
		let biggestValue = 0;
		let tempValue = 0;
		if (data.data) {
			data.data.forEach((value) => {
				tempValue += value.y;
				if (value.y > biggestValue) {
					biggestValue = value.y;
				}
				if (value.y < smallestValue) {
					smallestValue = value.y;
				}
			});

			// scale needs to output one rank lower than the lowest value
			const firstIndex = availableValues.findIndex((v) => v > smallestValue) - 1;
			// scale needs to output one rank higher that the biggest value
			const lastIndex = availableValues.findIndex((n) => n > biggestValue);
			const scaleYValues = availableValues.filter((_, index) => index >= firstIndex && index <= lastIndex);
			return {
				scaleYValues,
				totalValue: tempValue,
			};
		}
	}

	private yScaleLabels(data: IGraphData) {
		return this.generateGridYValues(data).scaleYValues;
	}
	private minValue(data: IGraphData) {
		return data.data ? this.generateGridYValues(data).scaleYValues[0] : 0;
	}
	private maxValue(data: IGraphData) {
		return data.data
			? this.generateGridYValues(data).scaleYValues[this.generateGridYValues(data).scaleYValues.length - 1]
			: 0;
	}

	render() {
		if (typeof window === 'undefined') {
			return null;
		}
		const { data, type, t, currencyKey } = this.props;
		const formatOptions = {
			notation: 'compact',
			compactDisplay: 'short',
		};

		const tooltip = (children: React.PropsWithChildren<PointTooltipProps>): JSX.Element => {
			return (
				<div className="graphTooltipContainer d-flex flex-column justify-content-center align-items-center">
					<p className="p14">{children.point.data.x}</p>
					<p className="p14">{`${
						type === 'sales' ? formatCurrency(currencyKey, children.point.data.y as number) : children.point.data.y
					}`}</p>
				</div>
			);
		};

		return (
			<div style={{ position: 'relative', height: '530px' }} className="shopSalesGraph">
				<div style={{ position: 'absolute', width: '100%', height: '530px' }}>
					<div className="graphHeaderContainer">
						{type === 'sales' ? (
							<h2>{formatCurrency(currencyKey, this.generateGridYValues(data[0]).totalValue)}</h2>
						) : (
							<h2>{`${this.generateGridYValues(data[0]).totalValue}`}</h2>
						)}
					</div>
					<ResponsiveLine
						data={data}
						margin={{ top: 50, right: 50, bottom: 180, left: 100 }}
						xScale={{ type: 'point' }}
						yScale={{
							type: 'linear',
							min: 0,
							max: this.maxValue(data[0]),
						}}
						curve="linear"
						axisTop={null}
						axisRight={null}
						axisBottom={{
							tickSize: 5,
							tickPadding: 5,
							legend: t('BSPortal_Common_Date'),
							legendOffset: 60,
							legendPosition: 'middle',
						}}
						axisLeft={{
							tickValues: this.yScaleLabels(data[0]),
							tickSize: 0,
							tickPadding: 10,
							legend:
								type === 'sales'
									? t('BSPortal_Common_Volume') + ' ' + `(${getCurrencySymbol(currencyKey)})`
									: t('User_ShopCard_Customers'),
							legendOffset: -70,
							legendPosition: 'middle',
							format: (v) => `${new Intl.NumberFormat('en', formatOptions as any).format(v)}`,
						}}
						theme={{
							fontSize: 14,
							fontFamily: 'IBMPlexSans',
							axis: {
								legend: {
									text: {
										fill: '#B1B1B1', // $gray-gigo
										fontSize: '14px',
										lineHeight: '16px',
										textTransform: 'uppercase',
										fontWeight: 400,
										letterSpacing: 1,
										fontFamily: 'IBMPlexSans',
									},
								},
							},
							crosshair: {
								line: {
									stroke: '#B1B1B1', // $gray-gigo
									strokeWidth: 1,
									strokeDasharray: '3 3',
								},
							},
						}}
						colors="#BDFF3B" // $green
						lineWidth={0}
						enableArea={true} // not working for "log" type of yScale
						areaOpacity={0.5}
						pointLabelYOffset={0}
						useMesh={true}
						enableGridX={false}
						enableGridY={true}
						isInteractive={true}
						enableCrosshair={true}
						crosshairType="x"
						pointSize={0}
						tooltip={(point) => tooltip(point)}
						animate={false} // disabled because of "@nivo/line": "^0.79.1" has a bug when animating
					/>
				</div>
			</div>
		);
	}
}

export default SalesInfoGraph;
